import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@vehix/portal/components/ui/sheet';
import { Button } from '@vehix/portal/components/ui/button';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Separator } from '@vehix/portal/components/ui/separator';
import { AlertTriangle, Pencil, Play, Trash2 } from 'lucide-react';
import { PipelineStatusBadge } from './PipelineStatusBadge';
import { formatInZone, formatTimeUntil, getNextRuns, tzAbbrev } from '../../utils/cronNextRun';
import { formatRelativeTime, formatTimestamp } from '../../utils/pipelineFormat';
import type { ScheduleRow } from '../../utils/scheduleRows';
import type { PipelineSchedule } from '../../services/pipelineOrchestrator';

interface ScheduleDetailSheetProps {
  row: ScheduleRow | null;
  now: Date;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (schedule: PipelineSchedule) => void;
  onTrigger: (schedule: PipelineSchedule) => void;
  onDelete: (schedule: PipelineSchedule) => void;
  isCoolingDown: (id: string) => boolean;
}

/** How many upcoming occurrences the panel lists. */
const UPCOMING_COUNT = 5;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className='grid grid-cols-[110px_1fr] items-start gap-2 py-1.5'>
    <span className='text-xs text-muted-foreground'>{label}</span>
    <div className='text-xs'>{children}</div>
  </div>
);

const HEADING_CLASS = 'mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground';

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className={HEADING_CLASS}>{children}</h3>
);

const UpcomingSection: React.FC<{ row: ScheduleRow; now: Date }> = ({ row, now }) => {
  const s = row.schedule;
  const upcoming = getNextRuns(s.cron_expression, s.time_zone, {
    from: now,
    count: UPCOMING_COUNT
  });

  if (!upcoming || upcoming.length === 0) {
    return (
      <section>
        <Heading>Upcoming runs</Heading>
        <p className='text-xs text-amber-600 dark:text-amber-500'>
          Can&apos;t predict runs from <code className='font-mono'>{s.cron_expression}</code>
        </p>
      </section>
    );
  }

  return (
    <section>
      <Heading>Upcoming runs</Heading>
      <ul className='space-y-1'>
        {upcoming.map((run, i) => (
          <li
            key={run.instant.toISOString()}
            className='flex items-baseline justify-between text-xs'
          >
            <span className={i === 0 ? 'font-medium' : 'text-muted-foreground'}>
              {formatInZone(run.instant, s.time_zone, 'ddd, MMM D · h:mm A')}{' '}
              {tzAbbrev(run.instant, s.time_zone)}
              {run.dstShifted && <AlertTriangle className='ml-1 inline h-3 w-3 text-amber-500' />}
            </span>
            {i === 0 && (
              <span className='text-muted-foreground'>{formatTimeUntil(run.instant, now)}</span>
            )}
          </li>
        ))}
      </ul>
      {!s.is_enabled && (
        <p className='mt-1 text-[11px] italic text-muted-foreground'>
          Paused — these would run if resumed.
        </p>
      )}
    </section>
  );
};

const ScopeSection: React.FC<{ row: ScheduleRow }> = ({ row }) => {
  const totalTasks = row.zones.reduce((n, z) => n + z.collectors.length, 0);

  return (
    <section>
      <Heading>Scope</Heading>
      {row.zones.length === 0 ? (
        <p className='text-xs text-muted-foreground'>No zones configured</p>
      ) : (
        <>
          <p className='mb-1 text-xs'>
            {row.zones.length} zone{row.zones.length === 1 ? '' : 's'} · {totalTasks} task
            {totalTasks === 1 ? '' : 's'}
          </p>
          <div className='flex flex-wrap gap-1'>
            {row.zones.map(z => (
              <Badge
                key={z.zoneSlug}
                variant='secondary'
                className='text-[10px]'
                title={z.collectors.join(', ')}
              >
                {z.label}
              </Badge>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const NotifySection: React.FC<{ schedule: PipelineSchedule }> = ({ schedule }) => {
  const recipients = schedule.notification_emails ?? [];
  const outcomes = [
    schedule.notify_on_success ? 'success' : null,
    schedule.notify_on_failure ? 'failure' : null
  ].filter(Boolean);

  return (
    <section>
      <Heading>Notifications</Heading>
      {recipients.length === 0 || outcomes.length === 0 ? (
        <p className='text-xs text-muted-foreground'>No email notifications configured</p>
      ) : (
        <>
          <p className='mb-1 text-xs'>Emails on {outcomes.join(' and ')}</p>
          <div className='flex flex-wrap gap-1'>
            {recipients.map(email => (
              <Badge key={email} variant='outline' className='text-[10px]'>
                {email}
              </Badge>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const ConfigSection: React.FC<{ row: ScheduleRow }> = ({ row }) => {
  const s = row.schedule;
  const ran = s.last_triggered_at || s.last_run_status;

  return (
    <section>
      <Field label='Cadence'>
        {row.cadence.text}
        <p className='mt-0.5 font-mono text-[10px] text-muted-foreground'>
          {s.cron_expression} · {s.time_zone}
        </p>
      </Field>
      <Field label='Last run'>
        {ran ? (
          <span className='flex items-center gap-2'>
            {s.last_run_status && <PipelineStatusBadge status={s.last_run_status} size='sm' />}
            <span className='text-muted-foreground' title={formatTimestamp(s.last_triggered_at)}>
              {formatRelativeTime(s.last_triggered_at)}
            </span>
          </span>
        ) : (
          <span className='text-muted-foreground'>Never run</span>
        )}
      </Field>
      <Field label='Run mode'>
        <span className='capitalize'>{s.pipeline_config.run_mode || 'auto'}</span>
      </Field>
      <Field label='Cooldown'>{s.min_interval_minutes} min between runs</Field>
    </section>
  );
};

/**
 * Everything a schedule does, without opening the edit form.
 *
 * The edit dialog was previously the only way to see a schedule's zones,
 * recipients or cooldown — which meant opening a mutable form just to read.
 * This is the read path.
 */
// eslint-disable-next-line max-lines-per-function
export const ScheduleDetailSheet: React.FC<ScheduleDetailSheetProps> = ({
  row,
  now,
  open,
  onOpenChange,
  onEdit,
  onTrigger,
  onDelete,
  isCoolingDown
}) => {
  if (!row) return null;

  const s = row.schedule;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side='right' className='w-full overflow-y-auto sm:max-w-md'>
        <SheetHeader>
          <SheetTitle className='pr-8 text-base'>{s.schedule_name}</SheetTitle>
          <SheetDescription className='flex items-center gap-2'>
            {s.tenants?.tenant_name ?? 'Unknown tenant'}
            <Badge variant={s.is_enabled ? 'default' : 'secondary'} className='text-[10px]'>
              {s.is_enabled ? 'Active' : 'Paused'}
            </Badge>
          </SheetDescription>
        </SheetHeader>

        {s.description && <p className='mt-3 text-xs text-muted-foreground'>{s.description}</p>}

        <div className='mt-4 space-y-4'>
          <UpcomingSection row={row} now={now} />
          <Separator />
          <ConfigSection row={row} />
          <Separator />
          <ScopeSection row={row} />
          <Separator />
          <NotifySection schedule={s} />
        </div>

        <div className='mt-6 flex gap-2'>
          <Button
            size='sm'
            variant='outline'
            className='flex-1'
            onClick={() => onTrigger(s)}
            disabled={isCoolingDown(s.id)}
          >
            <Play className='mr-1 h-3.5 w-3.5' />
            Run now
          </Button>
          <Button size='sm' className='flex-1' onClick={() => onEdit(s)}>
            <Pencil className='mr-1 h-3.5 w-3.5' />
            Edit
          </Button>
          <Button
            size='sm'
            variant='outline'
            className='text-red-500 hover:text-red-600'
            onClick={() => {
              onOpenChange(false);
              onDelete(s);
            }}
            title='Delete schedule'
          >
            <Trash2 className='h-3.5 w-3.5' />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
